import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtINR, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Barcode, PlusCircle, Package, Lock } from "lucide-react";
import RoleGuide from "@/components/RoleGuide";
import { useAuth } from "@/context/AuthContext";

export default function Lubricants() {
  return (
    <div className="space-y-4" data-testid="lub-root">
      <Toaster position="top-center" />
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Lubricants</h1>
      <RoleGuide guides={{
        manager: { freq: "weekly", title: "Buy stock → print barcodes → track sales", text: "Enter a purchase (product, qty, purchase price, selling price). After saving, a &quot;Print Barcodes&quot; button opens the label sheet for only the SKUs you just added. Stick the printed labels on the physical stock. Cashiers scan them at sale time." },
        cashier: { freq: "on sale", title: "Sell from stock", text: "Sales are captured on the Shift Entry form → Lubricants Sales row. Tap the Scan button and point the phone at the barcode on the bottle. Stock decrements automatically." },
        assistant_manager: { freq: "monthly", title: "Watch stock levels", text: "Stock tab flags low-stock items (< 2 months of avg sales) in amber and negative stock in red." },
      }} />
      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock">Stock</TabsTrigger>
          <TabsTrigger value="purchase">Purchases</TabsTrigger>
          <TabsTrigger value="wastage">Wastage</TabsTrigger>
        </TabsList>
        <TabsContent value="stock"><Stock /></TabsContent>
        <TabsContent value="purchase"><Purchases /></TabsContent>
        <TabsContent value="wastage"><Wastage /></TabsContent>
      </Tabs>
    </div>
  );
}

function Stock() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/manager/lub_stock").then((r) => setRows(r.data)); }, []);
  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm mt-3">
      <table className="dense-table w-full text-sm">
        <thead><tr><th>ID</th><th>Name</th><th>Opening</th><th>Purchased</th><th>Sold</th><th>Wasted</th><th>Stock</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.lub_id} className={r.stock < 0 ? "state-danger" : ""}>
              <td className="font-mono">{r.lub_id}</td>
              <td className="font-bold">{r.name}</td>
              <td className="num">{r.opening}</td>
              <td className="num">{r.purchased}</td>
              <td className="num">{r.sold}</td>
              <td className="num">{r.wasted}</td>
              <td className={"num font-bold " + (r.stock < 0 ? "text-red-600" : r.stock < 5 ? "text-amber-700" : "text-slate-900")}>{r.stock}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Purchases() {
  const { user } = useAuth();
  // Only manager+ can edit lubricant prices — filler / cashier are read-only.
  const canEditPrice = ["owner", "developer", "admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [lubs, setLubs] = useState([]);
  const [form, setForm] = useState({ date: todayISO(), lub_id: "", qty: 0, rate: 0, sell_price: 0, comment: "" });
  const [justAdded, setJustAdded] = useState([]);  // SKUs from recent purchases → offer to print
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", purchase_price: 0, sale_price: 0, litres_per_unit: 1 });

  const load = () => api.get("/manager/lub_purchases").then((r) => setRows(r.data));
  const loadLubs = () => api.get("/masters/lubricants").then((r) => setLubs(r.data));
  useEffect(() => { load(); loadLubs(); }, []);

  const selectedLub = lubs.find((l) => l.lub_id === form.lub_id);

  // Auto-fill rate + sell_price when a product is picked
  const pickProduct = (lub_id) => {
    const l = lubs.find((x) => x.lub_id === lub_id);
    setForm((f) => ({
      ...f,
      lub_id,
      rate: l?.purchase_price || f.rate || 0,
      sell_price: l?.sale_price || f.sell_price || 0,
    }));
  };

  const save = async () => {
    if (!form.lub_id) { toast.error("Pick a product"); return; }
    if (Number(form.qty) <= 0) { toast.error("Quantity must be > 0"); return; }
    try {
      await api.post("/manager/lub_purchases", form);
      // If the user changed the selling price on save, update the master too
      if (selectedLub && Number(form.sell_price) > 0 && Number(form.sell_price) !== Number(selectedLub.sale_price || 0)) {
        try { await api.patch(`/masters/lubricants/${form.lub_id}`, { sale_price: Number(form.sell_price), purchase_price: Number(form.rate) }); }
        catch (err) { console.debug("silent lubricant master patch failed", err?.response?.status); }
      }
      toast.success(`Added ${form.qty} × ${selectedLub?.name || form.lub_id}`);
      setJustAdded((s) => (s.includes(form.lub_id) ? s : [...s, form.lub_id]));
      setForm({ date: form.date, lub_id: "", qty: 0, rate: 0, sell_price: 0, comment: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addProduct = async () => {
    if (!newProduct.name.trim()) { toast.error("Product name required"); return; }
    try {
      const r = await api.post("/masters/lubricants", newProduct);
      toast.success(`Product created: ${newProduct.name}`);
      setShowNewProduct(false);
      setNewProduct({ name: "", purchase_price: 0, sale_price: 0, litres_per_unit: 1 });
      await loadLubs();
      setForm((f) => ({ ...f, lub_id: r.data?.lub_id || "" }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const printBarcodes = () => {
    const q = justAdded.join(",");
    const url = `/lubricant-labels?ids=${encodeURIComponent(q)}`;
    window.location.href = url;
  };

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 text-sm uppercase tracking-wider">Add Purchase</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Date</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Product</label>
            <select value={form.lub_id} onChange={(e) => pickProduct(e.target.value)} className="w-full h-11 border border-slate-300 rounded-sm px-2 bg-white" data-testid="lub-purchase-product">
              <option value="">— Product —</option>
              {lubs.map((l) => <option key={l.lub_id} value={l.lub_id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Qty</label>
            <Input type="number" step="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} className="h-11 num" data-testid="lub-purchase-qty" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1">
              Purchase ₹ {!canEditPrice && <Lock size={10} className="text-slate-400" />}
            </label>
            <Input type="number" step="0.01" value={form.rate}
                   onChange={(e) => setForm({ ...form, rate: Number(e.target.value) })}
                   readOnly={!canEditPrice}
                   className={"h-11 num" + (!canEditPrice ? " bg-slate-100 text-slate-500 cursor-not-allowed" : "")}
                   data-testid="lub-purchase-rate" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1">
              Selling ₹ {!canEditPrice && <Lock size={10} className="text-slate-400" />}
            </label>
            <Input type="number" step="0.01" value={form.sell_price}
                   onChange={(e) => setForm({ ...form, sell_price: Number(e.target.value) })}
                   readOnly={!canEditPrice}
                   className={"h-11 num" + (!canEditPrice ? " bg-slate-100 text-slate-500 cursor-not-allowed" : "")}
                   data-testid="lub-purchase-sell" />
          </div>
          <div className="col-span-full">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Note</label>
            <Input placeholder="Invoice # / vendor / etc." value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="h-11" />
          </div>
        </div>
        {selectedLub && (
          <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2">
            Line total: <strong>{fmtINR((Number(form.qty) || 0) * (Number(form.rate) || 0))}</strong>
            {" · "}Estimated margin per unit: <strong>{fmtINR(Number(form.sell_price || 0) - Number(form.rate || 0))}</strong>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <Button className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-11" onClick={save} data-testid="lub-purchase-save">Save Purchase</Button>
          <Button variant="outline" onClick={() => setShowNewProduct((v) => !v)} className="border-2 border-slate-300 h-11 font-bold" data-testid="lub-toggle-newprod"><PlusCircle size={14} className="mr-1" /> New product</Button>
          <Button variant="outline" onClick={() => toast("Old stock: paste an .xlsx/.csv with SKU, product, opening_qty, purchase_price, selling_price on the Masters → Lubricants page. Bulk-import flow is coming — for now use Masters lubricants to load opening stock, then this page for movements.", { duration: 6000 })} className="border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900 h-11 font-bold" data-testid="lub-oldstock-btn"><Package size={14} className="mr-1" /> Old stock upload</Button>
          {justAdded.length > 0 && (
            <Button onClick={printBarcodes} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11" data-testid="lub-print-barcodes-btn">
              <Barcode size={14} className="mr-1" /> Print barcodes ({justAdded.length})
            </Button>
          )}
        </div>
        {showNewProduct && (
          <div className="mt-3 border-2 border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="new-product-panel">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">Add a new lubricant SKU</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-bold text-slate-500">Name</label>
                <Input value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="e.g. 1Ltr Synthetic 5w/30" className="h-11" data-testid="np-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Purchase ₹</label>
                <Input type="number" step="0.01" value={newProduct.purchase_price} onChange={(e) => setNewProduct({ ...newProduct, purchase_price: Number(e.target.value) })} className="h-11 num" data-testid="np-purchase" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Selling ₹</label>
                <Input type="number" step="0.01" value={newProduct.sale_price} onChange={(e) => setNewProduct({ ...newProduct, sale_price: Number(e.target.value) })} className="h-11 num" data-testid="np-sale" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Litres / unit</label>
                <Input type="number" step="0.01" value={newProduct.litres_per_unit} onChange={(e) => setNewProduct({ ...newProduct, litres_per_unit: Number(e.target.value) })} className="h-11 num" />
              </div>
              <Button onClick={addProduct} className="bg-slate-900 hover:bg-slate-800 text-white h-11 font-bold col-span-full sm:col-auto" data-testid="np-save">Create SKU</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Purchase history</h3>
          <Link to="/lubricant-labels" className="text-xs text-slate-700 underline hover:text-slate-900 flex items-center gap-1"><Barcode size={12} /> All barcode labels →</Link>
        </div>
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Date</th><th>Product</th><th>Qty</th><th>Rate ₹</th><th>Total ₹</th></tr></thead>
          <tbody>
            {rows.slice(0, 30).map((r) => {
              const nm = lubs.find((l) => l.lub_id === r.lub_id)?.name || r.lub_id;
              return (
                <tr key={r.id}>
                  <td>{r.date}</td>
                  <td>{nm}</td>
                  <td className="num">{r.qty}</td>
                  <td className="num">{fmtINR(r.rate)}</td>
                  <td className="num font-bold">{fmtINR(Number(r.qty || 0) * Number(r.rate || 0))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Wastage() {
  const [rows, setRows] = useState([]);
  const [lubs, setLubs] = useState([]);
  const [form, setForm] = useState({ lub_id: "", qty: 0, reason: "" });
  const load = () => api.get("/manager/lub_wastage").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/masters/lubricants").then((r) => setLubs(r.data));
  }, []);
  const save = async () => {
    try { await api.post("/manager/lub_wastage", form); toast.success("Recorded"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select value={form.lub_id} onChange={(e) => setForm({ ...form, lub_id: e.target.value })} className="h-11 border border-slate-300 rounded-sm px-2 bg-white col-span-2">
            <option value="">— Product —</option>
            {lubs.map((l) => <option key={l.lub_id} value={l.lub_id}>{l.name}</option>)}
          </select>
          <Input type="number" step="0.01" placeholder="Qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} className="h-11 num" />
          <Input placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="h-11" />
        </div>
        <Button className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={save}>Record</Button>
      </Card>
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>When</th><th>Product</th><th>Qty</th><th>Reason</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}><td>{r.datetime?.slice(0, 10)}</td><td>{r.lub_id}</td><td className="num">{r.qty}</td><td>{r.reason}</td></tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
